import { inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { signalStoreFeature, type, withMethods } from '@ngrx/signals'
import { Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { catchError, EMPTY, exhaustMap, groupBy, map, mergeMap, pipe, switchMap, takeUntil, tap } from 'rxjs'

import { isPaymentRequired } from '@app/core/is-payment-required'
import { nonCancelableRequest } from '@app/core/rxjs/non-cancelable-request'
import { CreditsStore } from '@app/modules/credits/store/credits.store'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import type { ConversationState } from '@app/modules/messaging/components/store/conversation.store'
import { CONVERSATION_STORE_CONFIG } from '@app/modules/messaging/components/store/conversation.store'
import { injectDefaultErrorHandler } from '@app/modules/messaging/components/store/default-error-handler'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { Message } from '@app/modules/messaging/models/message.model'
import { ConversationService } from '@app/modules/messaging/services/conversation.service'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'

import { voiceMessageEvents } from './voice-message.events'

export const voiceMessageFeature = () =>
  signalStoreFeature(
    { state: type<ConversationState>() },
    withMethods(
      (
        _store,
        creditsNavigationService = inject(CreditsNavigationService),
        messagingService = inject(MessagingService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        dispatch = injectDispatch(voiceMessageEvents),
        conversationService = inject(ConversationService),
        defaultErrorHandler = injectDefaultErrorHandler(),
      ) => ({
        attemptToUnlockVoiceMessage: (
          message: Message & { type: 'voice'; status: 'locked' },
          conversation: Conversation,
        ) => {
          if (!messagingService.canAffordVoiceMessageUnlock(conversation)) {
            creditsNavigationService.visitCredits('unlockVoiceMessage', {
              source,
              profile_id: conversation.profile.id,
            })
            return
          }
          dispatch.unlock({ message, profile_id: conversation.profile.id })
        },

        playVoiceMessage: rxMethod<{
          message: Message & { type: 'voice'; status: 'viewable' }
          profile_browse_id: string
        }>(
          pipe(
            groupBy(({ message }) => message.timestamp),
            mergeMap(group =>
              group.pipe(
                switchMap(({ message, profile_browse_id }) =>
                  conversationService.play(message.timestamp, source, profile_browse_id).pipe(
                    catchError(error => {
                      defaultErrorHandler(error)
                      return EMPTY
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),
      }),
    ),

    withEventHandlers(
      (
        _store,
        conversationService = inject(ConversationService),
        creditsNavigationService = inject(CreditsNavigationService),
        creditsStore = inject(CreditsStore),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        defaultErrorHandler = injectDefaultErrorHandler(),
        events = inject(Events),
        dispatch = injectDispatch(voiceMessageEvents),
      ) => ({
        unlock$: events.on(voiceMessageEvents.unlock).pipe(
          map(({ payload }) => payload),
          groupBy(({ message }) => message.timestamp),
          mergeMap(group =>
            group.pipe(
              exhaustMap(({ message, profile_id }) =>
                conversationService.unlockVoiceMessage(message.timestamp, source).pipe(
                  tap(response => creditsStore.onCreditsSpent(response)),
                  nonCancelableRequest(),
                  tapResponse({
                    next: response => dispatch.unlockSuccess({ response }),
                    error: error => dispatch.unlockFailure({ error, profile_id }),
                    finalize: () => dispatch.unlockEnd({ messageTimestamp: message.timestamp }),
                  }),
                  takeUntil(events.on(conversationEvents.select)),
                ),
              ),
            ),
          ),
        ),

        onUnlockFailure$: events.on(voiceMessageEvents.unlockFailure).pipe(
          map(({ payload }) => payload),
          tap(({ error, profile_id }) => {
            if (isPaymentRequired(error)) {
              return creditsNavigationService.visitCredits('unlockVoiceMessage', { source, profile_id })
            }

            defaultErrorHandler(error)
          }),
        ),
      }),
    ),
  )
